import ConfettiBurst from "@/components/ConfettiBurst"
import { lumi } from "@/lib/lumi"
import {ArrowRight, Check, Loader2, Mail, Zap} from 'lucide-react'
import { FormEvent, useEffect, useState } from "react"
import { useHead } from "@unhead/react"

const HERO_IMAGE = "https://static.lumi.new/f7/f76e0a6879fd5803714594dfeac80144.webp"
const LOGO_IMAGE = "https://static.lumi.new/b4/b4bc0a849c80dbe75178d1417ea1d30f.webp"
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const JOINED_KEY = "penguin-pad-joined"

function Home() {
  const [email, setEmail] = useState("")
  const [error, setError] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [joined, setJoined] = useState(false)
  const [burstId, setBurstId] = useState(0)

  useEffect(() => {
    try {
      if (window.localStorage.getItem(JOINED_KEY) === "1") {
        setJoined(true)
      }
    } catch {
      // Ignore private-mode storage failures.
    }
  }, [])

  useHead({
    title: "Penguin Pad Coming Soon",
    meta: [
      {
        name: "description",
        content: "Penguin Pad is Robinhood Chain's bonding-curve presale launchpad. Join the waitlist and be first on the ice.",
      },
    ],
  })

  const handleJoin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (joined || submitting) return

    const nextEmail = email.trim().toLowerCase()
    if (!EMAIL_PATTERN.test(nextEmail)) {
      setError("Enter a valid email so we can ping you when bonding-curve presales open.")
      return
    }

    setError("")
    setSubmitting(true)
    const now = new Date().toISOString()

    try {
      await lumi.entities.waitlist_signups.create({
        email: nextEmail,
        source: "coming-soon",
        status: "subscribed",
        creator: nextEmail,
        createdAt: now,
        updatedAt: now,
      })

      try {
        window.localStorage.setItem(JOINED_KEY, "1")
      } catch {
        // Ignore private-mode storage failures and still celebrate the join.
      }
      setJoined(true)
      setBurstId((current) => current + 1)
    } catch (saveError) {
      console.error("Waitlist save failed:", saveError)
      setError("Could not save your email. Try again in a moment.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#071018] text-white">
      <ConfettiBurst burstId={burstId} />

      <img
        src={HERO_IMAGE}
        alt="Cool penguin lounging in a neon igloo arcade called Penguin Pad"
        className="absolute inset-0 h-full w-full object-cover object-[22%_center] sm:object-[28%_center] lg:object-center"
      />

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_40%,transparent_0%,rgba(4,10,16,0.18)_42%,rgba(4,10,16,0.78)_100%)]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#041018] via-transparent to-[#041018]/55" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#041018]/70 to-transparent" />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-6xl flex-col px-5 py-6 sm:px-8 lg:px-10">
        <header className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 flex-col">
            <img
              src={LOGO_IMAGE}
              alt="Penguin Pad"
              className="h-12 w-auto max-w-[220px] object-contain object-left sm:h-14 sm:max-w-[280px]"
            />
            <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-cyan-100/80">
              Bonding-curve presale launchpad
            </p>
          </div>
          <span className="rounded-full border border-cyan-200/30 bg-black/35 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-100 backdrop-blur-md">
            Waitlist open
          </span>
        </header>

        <main className="flex flex-1 flex-col justify-end pb-4 pt-16 lg:justify-center lg:pb-8 lg:pt-8">
          <section className="waitlist-panel ml-auto w-full max-w-xl rounded-[28px] border border-white/15 bg-[#07151c]/72 p-5 shadow-[0_24px_80px_rgba(0,0,0,0.45)] backdrop-blur-xl sm:p-8">
            <div className="mb-5 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-[#C8F542] px-3 py-1 text-[11px] font-black uppercase tracking-[0.2em] text-[#132109]">
                Coming soon
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-cyan-300/30 px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-cyan-100">
                <Zap className="h-3 w-3" />
                Robinhood Chain
              </span>
            </div>

            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-[#C8F542]">
              Bonding curve &middot; Presale launchpad
            </p>
            <h1 className="font-display text-[42px] leading-[0.9] text-[#C8F542] drop-shadow-[0_4px_0_#101418] sm:text-6xl">
              The pad is
              <br />
              warming up.
            </h1>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-cyan-50/85 sm:text-base">
              Penguin Pad is both: a bonding-curve engine and a presale launchpad on Robinhood Chain. Early birds ride the curve — not the leftovers.
            </p>

            {joined ? (
              <div className="waitlist-panel mt-6 flex items-start gap-3 rounded-2xl border border-[#C8F542]/40 bg-[#C8F542]/12 px-4 py-4">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#C8F542] text-[#132109]">
                  <Check className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-semibold text-[#C8F542]">You&apos;re on the ice.</p>
                  <p className="mt-1 text-sm text-cyan-50/80">
                    Confetti locked. We&apos;ll ping you when the bonding-curve presale launchpad opens.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleJoin} className="mt-6">
                  <label htmlFor="waitlist-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-cyan-100/80">
                    Join the pad
                  </label>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <div className="relative flex-1">
                      <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-cyan-100/50" />
                      <input
                        id="waitlist-email"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(event) => {
                          setEmail(event.target.value)
                          if (error) setError("")
                        }}
                        placeholder="you@wallet.mail"
                        className="h-12 w-full rounded-2xl border border-white/15 bg-black/35 pl-11 pr-4 text-sm text-white outline-none placeholder:text-white/35 focus:border-[#C8F542] focus:ring-2 focus:ring-[#C8F542]/30"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-[#C8F542] px-5 text-sm font-black uppercase tracking-[0.14em] text-[#132109] shadow-[0_10px_0_#6ea00f] transition hover:translate-y-[1px] hover:shadow-[0_8px_0_#6ea00f] focus:outline-none focus:ring-2 focus:ring-[#C8F542] focus:ring-offset-2 focus:ring-offset-[#07151c] disabled:cursor-wait disabled:opacity-70"
                    >
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      {submitting ? "Joining" : "Join"}
                      {submitting ? null : <ArrowRight className="h-4 w-4" />}
                    </button>
                  </div>
                  {error ? <p className="mt-3 text-sm text-rose-300">{error}</p> : (
                    <p className="mt-3 text-xs text-cyan-100/60">No spam. Just first-in alerts for bonding-curve presales.</p>
                  )}
              </form>
            )}

            <dl className="mt-7 grid grid-cols-3 gap-3 border-t border-white/10 pt-5 text-center">
              <div>
                <dt className="text-[10px] uppercase tracking-[0.18em] text-cyan-100/55">Curve</dt>
                <dd className="mt-1 text-sm font-semibold text-white">Bonding</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-[0.18em] text-cyan-100/55">Pad</dt>
                <dd className="mt-1 text-sm font-semibold text-white">Presale</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-[0.18em] text-cyan-100/55">Type</dt>
                <dd className="mt-1 text-sm font-semibold text-white">Launchpad</dd>
              </div>
            </dl>
          </section>
        </main>
      </div>
    </div>
  )
}

export default Home
